import { useProductsStore } from '~/store/products.store';

import { ISOPROConcreteCovering, ISOPROHeight, ISOPROProductDTO } from './product.types';

const DEFAULT_IMAGE =
  "https://blackmantkd.com/wp-content/uploads/2017/04/default-image-620x600.jpg";
const DEFAULT_LENGTH = 1000;
const DEFAULT_CONCRETE_COVERING = ISOPROConcreteCovering.cv35;
const DEFAULT_INSULATION_THICKNESS = 120;
const DEFAULT_NUMBER_OF_THRUST_BEARINGS = 0;

const HEAT_FLOW_FACTOR = 0.0261;
const HEAT_FLOW_COUNTER = 0.12;

export class ISOPROProduct {
  readonly type: ISOPROProductDTO["type"];
  readonly rowKey: ISOPROProductDTO["rowKey"];
  readonly expansionJointsDistance: ISOPROProductDTO["expansionJointsDistance"];
  readonly height: ISOPROProductDTO["height"];
  readonly capacity: ISOPROProductDTO["capacity"];

  readonly timestamp: ISOPROProductDTO["timestamp"];
  readonly heatFlow: ISOPROProductDTO["heatFlow"] | null = null;
  readonly hasFireProtection: boolean;
  readonly fireProtectionType: ISOPROProductDTO["fireProtectionType"]

  readonly imageURL: string = DEFAULT_IMAGE;

  properties: {
    dimensions: {
      /** in mm */
      length: NonNullable<ISOPROProductDTO["length"]>;
      /** in mm */
      readonly insulationThickness: number;
      /** in mm */
      readonly concreteCovering: ISOPROConcreteCovering;
    };

    construction: {
      thrustBearings: {
        numberOfThrustBearings: ISOPROProductDTO["numberOfThrustBearing"];
        kindOfThrustBearing: ISOPROProductDTO["kindOfThrustBearing"];
        numberOfThrustShearPosBearing: ISOPROProductDTO['numberOfThrustShearPosBearing'];
        // numberOfThrustShearNegBearing: ISOPROProductDTO['numberOfThrustShearNegBearing'];
      };
      tensionRods: { };
    };
  };

  constructor (data: ISOPROProductDTO) {
    this.timestamp = data.timestamp;
    this.type = data.type;
    this.expansionJointsDistance = data.expansionJointsDistance;
    this.height = data.height;
    this.hasFireProtection = data.hasFireProtection;
    this.fireProtectionType = data.fireProtectionType;
    this.capacity = data.capacity;

    this.rowKey = this.hasFireProtection
      ? this.appendRowKeyLength(data,`${data.rowKey.split("R")[0].trim()} ${data.fireProtectionType}`)
      : data.rowKey;
    this.heatFlow = data.heatFlow || null;
    this.imageURL = `./assets/productImages/${this.type}.png`;

    this.properties = {
      dimensions: {
        concreteCovering: DEFAULT_CONCRETE_COVERING,
        insulationThickness: DEFAULT_INSULATION_THICKNESS,
        length: data.length || DEFAULT_LENGTH,
      },
      construction: {
        thrustBearings: {
          numberOfThrustBearings:
            data.numberOfThrustBearing || DEFAULT_NUMBER_OF_THRUST_BEARINGS,
          kindOfThrustBearing: data.kindOfThrustBearing,
          numberOfThrustShearPosBearing: data.numberOfThrustShearPosBearing
        },
        tensionRods: { },
      },
    };
  }

  get lambdaValue (): number | null {
    if (!this.heatFlow) return null;
    return (
      HEAT_FLOW_COUNTER /
      (this.height / 1000 / this.heatFlow - HEAT_FLOW_FACTOR)
    );
  }

  appendRowKeyLength (data: ISOPROProductDTO, rowKey: string): string {
    if (data.length === 1000) return rowKey;
    return `${rowKey}` + ` L${data.length}`;
  }

  getBiggerLoadBearingCapacityProduct (): ISOPROProduct | null {
    try {
      const newRowKey = `${this.type}${this.capacity + 10}`;
      const rowKey = this.rowKey.replace(
        this.type + this.capacity,
        newRowKey
      );
      return (useProductsStore().productsMap.get(rowKey) as ISOPROProduct) || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  getSmallerHeightProduct (): ISOPROProduct | null {
    try {
      const smaller = `NUMBER_${this.height - 10}` as keyof typeof ISOPROHeight;
      const current = `NUMBER_${this.height}` as keyof typeof ISOPROHeight;
      const rowKey = this.rowKey.replace(
        `${ISOPROHeight[current]}`,
        `${ISOPROHeight[smaller]}`
      );
      return (useProductsStore().productsMap.get(rowKey) as ISOPROProduct) || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  getBiggerShearForceCapacityProduct (): ISOPROProduct | null {
    return null;
  }

  getOneWithoutFireProtection (): ISOPROProduct | null {
    return null;
  }
}

